export function toLeaderboard(results = [], config = []) {
  const cfgBySeries = Object.fromEntries((config || []).map((c) => [c.series_name, c]));
  const bySeries = {};
  for (const r of results || []) {
    (bySeries[r.series_name] = bySeries[r.series_name] || []).push(r);
  }
  return Object.entries(bySeries).map(([series, ladder]) => {
    const ok = ladder.filter((r) => r.status === "ok" && r.smape != null);
    // Only the ok set (status "ok", valid smape) is eligible to win — a non-ok
    // row flagged is_winner must not be chosen.
    // keyOf prefers the primary selection score when present, falls back to smape.
    const keyOf = (r) => (r.score != null ? r.score : r.smape);
    const winner = ok.find((r) => r.is_winner) || ok.slice().sort((a, b) => keyOf(a) - keyOf(b))[0] || null;
    const runnerUp = ok.filter((r) => r !== winner).sort((a, b) => keyOf(a) - keyOf(b))[0] || null;
    return {
      series,
      winner,
      runnerUp,
      ladder,
      source: cfgBySeries[series]?.source || "auto",
      // The model currently powering the live forecast (last promoted), so the UI
      // can show what a Promote would change.
      live: cfgBySeries[series]?.algorithm || null,
    };
  });
}

// A selection score is only trustworthy in a bounded range. On near-flat / near-
// zero series the naive-error denominator collapses and the score diverges toward
// 1e21+, which JS then renders in exponential notation. Treat anything non-finite
// or wildly large as unstable → shown as "—", never as a confident winner.
const SCORE_STABLE_MAX = 100;
export function isStableScore(v) {
  return typeof v === "number" && Number.isFinite(v) && Math.abs(v) < SCORE_STABLE_MAX;
}

// formatScore renders a selection score, guarding the unstable blow-ups above.
export function formatScore(v) {
  if (v == null || !isStableScore(v)) return "—";
  return v.toFixed(2);
}

// qualityOf rates how much to trust a row's winning model, primarily from sMAPE,
// with an unstable selection score overriding to "Unreliable". Tones map to the
// app's status palette (ok / attention / critical / neutral).
export function qualityOf(row) {
  const w = row && row.winner;
  if (!w) return { level: "none", label: "No model", tone: "neutral" };
  const score = w.score_out_of_sample != null ? w.score_out_of_sample : w.score;
  if (score != null && !isStableScore(score)) {
    return { level: "unreliable", label: "Unreliable", tone: "critical" };
  }
  const s = w.smape;
  if (s == null) return { level: "unrated", label: "Unrated", tone: "neutral" };
  if (s <= 0.1) return { level: "good", label: "Good", tone: "ok" };
  if (s <= 0.25) return { level: "fair", label: "Fair", tone: "attention" };
  return { level: "poor", label: "Poor", tone: "critical" };
}

// parentKey strips the "||<unit>" suffix so unit series fold under their resource.
export function parentKey(series) {
  return String(series == null ? "" : series).split("||")[0];
}

// groupByParent buckets rows by their resource parent. Each group has the parent
// series row (when present) and its per-unit children, preserving input order.
export function groupByParent(rows = []) {
  const map = new Map();
  for (const r of rows || []) {
    const key = parentKey(r.series);
    if (!map.has(key)) map.set(key, { key, parent: null, children: [] });
    const g = map.get(key);
    if (r.series === key) g.parent = r;
    else g.children.push(r);
  }
  return Array.from(map.values());
}

// promoteDiff summarises what a Promote would change vs the currently live models,
// so the confirmation can warn before pushing to the dashboard. Overrides already
// live in config, so only auto winners that differ (or new series) count.
export function promoteDiff(rows = []) {
  const changingRows = (rows || []).filter(
    (r) => r.winner && r.source !== "override" && r.winner.algorithm !== r.live
  );
  const poor = changingRows.filter((r) => {
    const q = qualityOf(r).level;
    return q === "poor" || q === "unreliable";
  });
  return {
    total: (rows || []).length,
    changing: changingRows.length,
    poor: poor.length,
    changingRows,
  };
}

// hasLeaderboardData is true when a row produced a usable result (a winning model
// from at least one valid backtest). Empty per-unit series — no winner — add no
// value to the table, so it hides them. An explicit user override is always kept.
export function hasLeaderboardData(row) {
  return !!(row && (row.winner || row.source === "override"));
}

// visibleLeaderboardRows drops the no-data rows from a leaderboard.
export function visibleLeaderboardRows(rows = []) {
  return (rows || []).filter(hasLeaderboardData);
}
